import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { format } from 'date-fns';

const TYPE_CONFIG = {
  ticket:  { label: '🎫 Ticket',       bg: '#dbeafe', color: '#1e40af' },
  sa:      { label: '💰 Salary Adv',   bg: '#dcfce7', color: '#166534' },
  login:   { label: '🔐 Login',        bg: '#fef9c3', color: '#854d0e' },
  user:    { label: '👤 User Mgmt',    bg: '#fce7f3', color: '#9d174d' },
};

const ACTION_COLORS = {
  'ticket created':  '#1A9E6B',
  'status changed':  '#1B8FD4',
  'assigned':        '#F4873A',
  'reply':           '#0E5F94',
  'note':            '#E8A020',
  'closed':          '#6B6B80',
  'resolved':        '#1A9E6B',
  'approved':        '#1A9E6B',
  'declined':        '#D63A3A',
  'login':           '#854d0e',
  'created':         '#1A9E6B',
  'updated':         '#F4873A',
  'deleted':         '#D63A3A',
  'password reset':  '#9d174d',
  'reset':           '#9d174d',
};

function getActionColor(action = '') {
  const lower = action.toLowerCase();
  for (const key of Object.keys(ACTION_COLORS)) {
    if (lower.includes(key)) return ACTION_COLORS[key];
  }
  return '#6B8CAE';
}

function getType(log) {
  if (log.type) return log.type;
  // Fallback detection from action text
  const a = (log.action || '').toLowerCase();
  if (a.includes('login')) return 'login';
  if (a.includes('user')) return 'user';
  if (a.includes('sa') || a.includes('salary')) return 'sa';
  return 'ticket';
}

export default function AuditLog() {
  const navigate = useNavigate();
  const [logs, setLogs]       = useState([]);
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal]     = useState(0);
  const [pages, setPages]     = useState(1);
  const [filter, setFilter]   = useState({
    user_id: '', type: '', date_from: '', date_to: '', page: 1,
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.user_id)   params.append('user_id',   filter.user_id);
      if (filter.type)      params.append('type',      filter.type);
      if (filter.date_from) params.append('date_from', filter.date_from);
      if (filter.date_to)   params.append('date_to',   filter.date_to);
      params.append('page',  filter.page);
      params.append('limit', 25);
      const res = await api.get(`/api/audit?${params}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => {
    api.get('/api/users').then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  const exportCSV = () => {
    const BOM = '\uFEFF';
    const headers = ['Date', 'Time', 'Type', 'Reference', 'Action', 'Performed By', 'Role', 'Detail'];
    const rows = logs.map(l => {
      const type = getType(l);
      const detail = l.old_value && l.new_value
        ? `${l.old_value} → ${l.new_value}`
        : l.new_value || l.old_value || '';
      return [
        format(new Date(l.created_at), 'dd/MM/yyyy'),
        format(new Date(l.created_at), 'HH:mm:ss'),
        TYPE_CONFIG[type]?.label.replace(/^\S+\s/, '') || type,
        l.ref_number || '',
        l.action || '',
        l.user_name || 'System',
        l.user_role ? l.user_role.replace(/_/g, ' ') : '',
        detail,
      ];
    });
    const csv = BOM + [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `OMIYE-MFB-AuditLog-${format(new Date(), 'dd-MM-yyyy')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilter = () => setFilter({ user_id: '', type: '', date_from: '', date_to: '', page: 1 });

  return (
    <div>
      {/* Header */}
      <div className="section-head" style={{ marginBottom: 16 }}>
        <div>
          <div className="section-title">📋 Audit Log</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
            All system activity — tickets, SA requests, logins, user management
          </div>
        </div>
        <button className="btn btn-success btn-sm" onClick={exportCSV}>📥 Export CSV</button>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Staff Member</label>
            <select className="form-select" value={filter.user_id}
              onChange={e => setFilter({ ...filter, user_id: e.target.value, page: 1 })}>
              <option value="">All Staff</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Activity Type</label>
            <select className="form-select" value={filter.type}
              onChange={e => setFilter({ ...filter, type: e.target.value, page: 1 })}>
              <option value="">All Types</option>
              <option value="ticket">🎫 Tickets</option>
              <option value="sa">💰 Salary Advance</option>
              <option value="login">🔐 Logins</option>
              <option value="user">👤 User Management</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">From Date</label>
            <input className="form-input" type="date" value={filter.date_from}
              onChange={e => setFilter({ ...filter, date_from: e.target.value, page: 1 })} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">To Date</label>
            <input className="form-input" type="date" value={filter.date_to}
              onChange={e => setFilter({ ...filter, date_to: e.target.value, page: 1 })} />
          </div>
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-primary btn-sm" onClick={fetchLogs}>🔍 Apply Filter</button>
          <button className="btn btn-ghost btn-sm" onClick={clearFilter}>Clear</button>
          <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 'auto' }}>
            {total} record{total !== 1 ? 's' : ''} found
          </span>
        </div>
      </div>

      {/* Log Table */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date & Time</th>
              <th>Type</th>
              <th>Reference</th>
              <th>Action</th>
              <th>Performed By</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>⏳ Loading…</td></tr>
            )}
            {!loading && logs.length === 0 && (
              <tr><td colSpan={6}>
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <div>No audit logs found</div>
                </div>
              </td></tr>
            )}
            {logs.map(log => {
              const type = getType(log);
              const tc = TYPE_CONFIG[type] || { label: type, bg: '#f3f4f6', color: '#374151' };
              const canNav = (type === 'ticket' || type === 'sa') && log.ref_id;
              const navPath = type === 'sa' ? `/sa/${log.ref_id}` : `/tickets/${log.ref_id}`;
              const showDetail = log.old_value || log.new_value;
              const isUUID = v => v && /[0-9a-f]{8}-/.test(v);

              return (
                <tr key={`${type}-${log.id}`} style={{ cursor: 'default' }}>
                  <td style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                    <div>{format(new Date(log.created_at), 'dd MMM yyyy')}</div>
                    <div style={{ fontSize: 11 }}>{format(new Date(log.created_at), 'HH:mm:ss')}</div>
                  </td>
                  <td>
                    <span style={{
                      background: tc.bg, color: tc.color,
                      padding: '3px 8px', borderRadius: 10,
                      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap'
                    }}>
                      {tc.label}
                    </span>
                  </td>
                  <td>
                    {log.ref_number ? (
                      <div>
                        <span
                          onClick={() => canNav && navigate(navPath)}
                          style={{
                            fontFamily: 'Sora', fontSize: 11, fontWeight: 700,
                            color: 'var(--sky-dark)',
                            cursor: canNav ? 'pointer' : 'default',
                            textDecoration: canNav ? 'underline' : 'none',
                          }}>
                          {log.ref_number}
                        </span>
                        {log.ref_subject && (
                          <div style={{ fontSize: 11, color: 'var(--muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.ref_subject}
                          </div>
                        )}
                      </div>
                    ) : <span style={{ color: 'var(--muted)' }}>—</span>}
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      fontSize: 12, fontWeight: 500, color: getActionColor(log.action)
                    }}>
                      <span style={{
                        width: 7, height: 7, borderRadius: '50%',
                        background: getActionColor(log.action), flexShrink: 0
                      }} />
                      {log.action}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{log.user_name || 'System'}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'capitalize' }}>
                      {log.user_role ? log.user_role.replace(/_/g, ' ') : ''}
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--muted)', maxWidth: 200 }}>
                    {showDetail && !isUUID(log.old_value) && !isUUID(log.new_value) ? (
                      log.old_value && log.new_value ? (
                        <span>
                          <span style={{ color: 'var(--danger)', textTransform: 'capitalize' }}>{log.old_value}</span>
                          {' → '}
                          <span style={{ color: 'var(--success)', textTransform: 'capitalize' }}>{log.new_value}</span>
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text3)' }}>{log.new_value || log.old_value}</span>
                      )
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button className="btn btn-ghost btn-sm" disabled={filter.page === 1}
            onClick={() => setFilter({ ...filter, page: filter.page - 1 })}>← Prev</button>
          <span style={{ padding: '4px 10px', fontSize: 12, color: 'var(--muted)' }}>
            Page {filter.page} of {pages}
          </span>
          <button className="btn btn-ghost btn-sm" disabled={filter.page >= pages}
            onClick={() => setFilter({ ...filter, page: filter.page + 1 })}>Next →</button>
        </div>
      )}
    </div>
  );
}
